import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, ChevronRight, Search, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

// Compound groups
const COMPOUND_GROUPS = [
  {
    label: "Peptides & Injectables",
    compounds: [
      "Tirzepatide", "Semaglutide", "Retatrutide", "Cagrilintide", "KPV", "VIP",
      "BPC-157", "BPC-157 + TB-500", "CJC-1295 (no DAC)", "CJC-1295 (no DAC) + Ipamorelin",
      "CJC-1295 (with DAC)", "TB-500 (Thymosin B4)", "AOD-9604", "PT-141", "5-Amino-1MQ",
      "Selank", "Semax", "Adamax", "N-Acetyl Semax", "N-Acetyl Selank",
      "N-Acetyl Epitalon Amidate", "P21", "Cerebrolysin", "Oxytocin Acetate", "Epitalon",
      "SS-31", "Tesamorelin", "Ipamorelin", "GHK-Cu", "Kisspeptin", "Thymosin Alpha",
      "MOTS-c", "LL-37", "DSIP",
    ],
  },
  {
    label: "Blends & Special Formulas",
    compounds: [
      "MNM (GHK + BPC + KPV)", "HHB Blend", "GGH Blend", "Super Human Blend",
      "Fat Blaster Lipo-C", "LC553 – Super Shred Blend", "RP226 – Relaxation PM",
    ],
  },
  {
    label: "Skin Boosters & Topicals",
    compounds: [
      "PDRN Skin Boost", "Periocular Peptide", "Pink Hyaluronic Acid",
      "Whitening Water Radiance", "Acne Removing Water", "Recombinant Collagen",
      "Spot Fading Skin Boost", "Topical AHK-Cu", "Topical GHK-Cu", "SNAP-8", "Matrixyl",
    ],
  },
  {
    label: "Vitamins, Aminos & Others",
    compounds: [
      "NAD", "L-Carnitine", "Glutathione", "Melatonin", "Lemon Bottle",
      "LC216", "SLUP 322", "Glow 70", "Klow 80", "Livagen",
    ],
  },
];

const ALL_COMPOUNDS = COMPOUND_GROUPS.flatMap((group) => group.compounds);

export function CompoundSelect({ value, onChange, placeholder = "Select compound...", className }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [keyboardHeight, setKeyboardHeight] = React.useState(0);
  const searchInputRef = React.useRef(null);
  const contentRef = React.useRef(null);

  // Reset search when closed
  React.useEffect(() => {
    if (!isOpen) setSearchValue("");
  }, [isOpen]);

  // Focus search when opened
  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard visibility using visualViewport API
  React.useEffect(() => {
    if (!isOpen) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      // Calculate keyboard height based on viewport difference
      const windowHeight = window.innerHeight;
      const viewportHeight = viewport.height;
      const newKeyboardHeight = windowHeight - viewportHeight;
      
      setKeyboardHeight(newKeyboardHeight > 50 ? newKeyboardHeight : 0);
    };

    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);
    
    // Initial check
    handleResize();

    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, [isOpen]);

  const handleSelect = (compound) => {
    onChange(compound);
    setIsOpen(false);
  };

  const handleCustomEntry = () => {
    if (searchValue.trim()) {
      onChange(searchValue.trim());
      setIsOpen(false);
    }
  };

  const filterCompounds = (compounds) => {
    if (!searchValue.trim()) return compounds;
    const search = searchValue.toLowerCase().trim();
    return compounds.filter((c) => c.toLowerCase().includes(search));
  };

  // Check if typed value exactly matches any compound (case-insensitive)
  const isExactMatch = searchValue.trim() && ALL_COMPOUNDS.some(
    (c) => c.toLowerCase() === searchValue.trim().toLowerCase()
  );

  // Show custom entry option when user types something and it's not an exact match
  const showCustomOption = searchValue.trim().length > 0 && !isExactMatch;

  // Calculate modal max height considering keyboard
  const modalMaxHeight = keyboardHeight > 0 
    ? `calc(100vh - ${keyboardHeight}px - 20px)` 
    : '85vh';

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn("w-full h-12 justify-between font-normal", !value && "text-muted-foreground", className)}
          data-testid="compound-select-trigger"
        >
          <span className="truncate text-left flex-1">{value || placeholder}</span>
          <ChevronRight className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-[100] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        
        {/* Content - adjusts for keyboard */}
        <DialogPrimitive.Content 
          ref={contentRef}
          className="fixed inset-x-0 z-[100] flex flex-col bg-background rounded-t-2xl shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-300"
          style={{
            bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : 0,
            maxHeight: modalMaxHeight,
            transition: 'bottom 0.15s ease-out, max-height 0.15s ease-out',
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <div className="w-12 h-1.5 rounded-full bg-muted-foreground/30" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <DialogPrimitive.Title className="text-lg font-semibold">
              Select Compound
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors">
              <X size={20} />
            </DialogPrimitive.Close>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30 shrink-0">
            <Search size={20} className="text-muted-foreground shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search or type custom..."
              className="flex-1 h-10 bg-transparent text-base outline-none placeholder:text-muted-foreground"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              enterKeyHint="done"
              data-testid="compound-search"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-muted"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Scrollable list - takes remaining space */}
          <div 
            className="flex-1 overflow-y-auto overscroll-contain min-h-0" 
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Custom entry option - shown when typing non-exact match */}
            {showCustomOption && (
              <button
                type="button"
                onClick={handleCustomEntry}
                className="w-full flex items-center gap-3 px-4 py-4 text-left border-b-2 border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors"
                data-testid="custom-compound-option"
              >
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Plus size={18} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-primary">Use custom compound</div>
                  <div className="text-sm text-muted-foreground truncate">"{searchValue.trim()}"</div>
                </div>
              </button>
            )}

            {/* Groups */}
            {COMPOUND_GROUPS.map((group) => {
              const filtered = filterCompounds(group.compounds);
              if (filtered.length === 0) return null;

              return (
                <div key={group.label}>
                  <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50 sticky top-0">
                    {group.label}
                  </div>
                  {filtered.map((compound) => {
                    const selected = value === compound;
                    return (
                      <button
                        key={compound}
                        type="button"
                        onClick={() => handleSelect(compound)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-4 text-left border-b border-border/50 transition-colors",
                          "hover:bg-accent active:bg-accent",
                          selected && "bg-primary/10"
                        )}
                        data-testid={`option-${compound.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <div className={cn(
                          "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                          selected ? "border-primary bg-primary" : "border-muted-foreground/40"
                        )}>
                          {selected && <Check size={12} className="text-primary-foreground" />}
                        </div>
                        <span className={selected ? "font-medium" : ""}>{compound}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {/* No results message with custom option */}
            {searchValue.trim() && COMPOUND_GROUPS.every(g => filterCompounds(g.compounds).length === 0) && (
              <div className="p-6 text-center text-muted-foreground">
                <p>No matching compounds found</p>
                <p className="text-sm mt-1">Tap above to use "{searchValue.trim()}" as custom</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {value && (
            <div className="px-4 py-3 border-t bg-muted/30 text-center text-sm text-muted-foreground shrink-0">
              Selected: <span className="font-semibold text-foreground">{value}</span>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default CompoundSelect;
